'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { allocationTransactionsColumnsDefs, PAGE_SIZE } from './config';
import { vaultV2AllocationTransactionsTableFeatures } from './config/VaultV2AllocationTransactions.columns';
import { useAllocationTransactionsData } from './hooks/useAllocationTransactionsData';
import { useAllocationTransactionsRow } from './hooks/useAllocationTransactionsRow';
export interface VaultV2AllocationTransactionsClientProps {
  address: Address;
  chainId: ChainId;
}
const VaultV2AllocationTransactionsClient = t0 => {
  const $ = _c(43);
  const {
    address,
    chainId
  } = t0;
  let t1;
  if ($[0] !== address || $[1] !== chainId) {
    t1 = {
      address,
      chainId,
      limit: PAGE_SIZE
    };
    $[0] = address;
    $[1] = chainId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    page,
    items,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useAllocationTransactionsData(t1);
  let t2;
  if ($[3] !== chainId || $[4] !== items) {
    t2 = {
      items,
      chainId
    };
    $[3] = chainId;
    $[4] = items;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const rows = useAllocationTransactionsRow(t2);
  let t3;
  if ($[6] !== rows) {
    t3 = {
      features: vaultV2AllocationTransactionsTableFeatures,
      data: rows,
      columns: allocationTransactionsColumnsDefs,
      enableSorting: false
    };
    $[6] = rows;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const allocationTransactionsTable = useTable(t3);
  const loading = isLoading || isLoadingNextPage;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs: allocationTransactionsColumnsDefs
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const skeletonRows = useSkeletonRows(t4);
  let t5;
  if ($[9] !== allocationTransactionsTable) {
    t5 = allocationTransactionsTable.getRowModel();
    $[9] = allocationTransactionsTable;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const hasRows = t5.rows.length > 0;
  const partial = page?.partial ?? false;
  const showError = !loading && !hasRows && (error != null || partial);
  const showEmpty = !loading && !hasRows && error == null && !partial;
  const showTable = loading || hasRows;
  let t6;
  if ($[11] !== hasRows || $[12] !== loading || $[13] !== partial) {
    t6 = partial && hasRows && !loading ? <p data-testid="vault-v2-allocation-transactions-partial-notice" className="text-secondary-foreground text-sm">Some allocation events could not be loaded, so this page may be incomplete.</p> : null;
    $[11] = hasRows;
    $[12] = loading;
    $[13] = partial;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== showError) {
    t7 = showError && <div data-testid="vault-v2-allocation-transactions-error"><ErrorDataUnavailable minHeight="340px" style={{
        borderRadius: "0"
      }} /></div>;
    $[15] = showError;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== canNextPage || $[18] !== pageIndex || $[19] !== showEmpty) {
    t8 = showEmpty && <TableNoResults text={pageIndex > 0 || canNextPage ? "No allocation events on this page." : "No allocation history found."} height="340px" style={{
      borderRadius: "0"
    }} />;
    $[17] = canNextPage;
    $[18] = pageIndex;
    $[19] = showEmpty;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== allocationTransactionsTable || $[22] !== loading || $[23] !== showTable || $[24] !== skeletonRows) {
    t9 = showTable && <Table.Table tableVariant="compact"><Table.Thead>{allocationTransactionsTable.getHeaderGroups().map(_temp)}</Table.Thead><Table.Tbody>{!loading && allocationTransactionsTable.getRowModel().rows.map(_temp2)}{loading && skeletonRows.map(_temp3)}</Table.Tbody></Table.Table>;
    $[21] = allocationTransactionsTable;
    $[22] = loading;
    $[23] = showTable;
    $[24] = skeletonRows;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t10 = <TableScrollArea className="grid h-full w-full">{t7}{t8}{t9}</TableScrollArea>;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const t11 = hasRows ? null : error;
  let t12;
  if ($[30] !== canNextPage || $[31] !== canPreviousPage || $[32] !== loading || $[33] !== nextPage || $[34] !== pageIndex || $[35] !== prefetchNext || $[36] !== previousPage || $[37] !== t11) {
    t12 = <CursorPagination loading={loading} error={t11} pageIndex={pageIndex} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[30] = canNextPage;
    $[31] = canPreviousPage;
    $[32] = loading;
    $[33] = nextPage;
    $[34] = pageIndex;
    $[35] = prefetchNext;
    $[36] = previousPage;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t10 || $[40] !== t12 || $[41] !== t6) {
    t13 = <div data-testid="vault-v2-allocation-transactions" className="gap-xs flex h-full w-full flex-col">{t6}{t10}{t12}</div>;
    $[39] = t10;
    $[40] = t12;
    $[41] = t6;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
export default VaultV2AllocationTransactionsClient;
function _temp(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp2(row) {
  return <TrBodyRow key={row.id} row={row} />;
}
function _temp3(row_0, index) {
  return <TrBodyRow key={`skeleton-allocation-transactions-${index}`} skeletonRow={row_0} />;
}